import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import { BiBookAlt, BiMedal, BiAward } from 'react-icons/bi';
import { settingsAPI } from '../../services/api';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { DEFAULT_PROGRAMS } from '../../utils/defaultPrograms';

const colorMap = {
  blue:      { grad: 'from-primary-500 to-primary-700',     bg: 'bg-primary-50',   border: 'border-primary-100',   icon: 'text-primary-600',   tag: 'text-primary-600'   },
  primary:   { grad: 'from-primary-600 to-primary-800',     bg: 'bg-primary-50',   border: 'border-primary-100',   icon: 'text-primary-600',   tag: 'text-primary-700'   },
  secondary: { grad: 'from-secondary-500 to-secondary-700', bg: 'bg-secondary-50', border: 'border-secondary-100', icon: 'text-secondary-600', tag: 'text-secondary-600' },
};
const icons = [BiBookAlt, BiMedal, BiAward, BiBookAlt];

const AcademicsSection = () => {
  const [programs, setPrograms] = useState(DEFAULT_PROGRAMS);
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const data = res.data?.data?.settings?.programs || [];
      if (data.length > 0) setPrograms(data);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="section-subtitle mb-2">Academic Programs</p>
          <h2 className="section-title mb-4">Our Educational Programs</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">
            Comprehensive academic programs designed to cater to every stage of a child's learning journey,
            from foundational years to career preparation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {programs.map((program, idx) => {
            const c = colorMap[program.colorScheme] || colorMap.primary;
            const Icon = icons[idx % icons.length];
            return (
              <motion.div
                key={program._id || idx}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: idx * 0.2 }}
                className={`${c.bg} border ${c.border} rounded-3xl overflow-hidden group card-hover`}
              >
                <div className={`h-2 bg-gradient-to-r ${c.grad}`} />
                <div className="p-5 sm:p-6 lg:p-8">
                  <div className={`w-14 h-14 ${c.bg} border ${c.border} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-7 h-7 ${c.icon}`} />
                  </div>
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-primary-500 font-serif mb-1">{program.title}</h3>
                    {program.grades && (
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.tag} border ${c.border}`}>
                        {program.grades}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">{program.description}</p>
                  {program.subjects?.length > 0 && (
                    <div className="mb-6">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Levels / Classes</p>
                      <div className="flex flex-wrap gap-2">
                        {program.subjects.slice(0, 4).map((s, sIdx) => (
                          <span key={sIdx} className="px-2 py-1 bg-white rounded-lg text-xs text-gray-600 border border-gray-200">{s}</span>
                        ))}
                        {program.subjects.length > 4 && (
                          <span className="px-2 py-1 bg-white rounded-lg text-xs text-gray-400 border border-gray-200">
                            +{program.subjects.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <Link to="/academics" className={`inline-flex items-center gap-2 text-sm font-semibold ${c.tag} group/link`}>
                    Learn More
                    <FiArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AcademicsSection;
